/* Lightweight OIDC SPA helper: PKCE code flow with a backend token exchange. */
import { api } from './api'

const b64url = (buf: ArrayBuffer) =>
  btoa(String.fromCharCode(...new Uint8Array(buf)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')

async function sha256(input: string) {
  const enc = new TextEncoder()
  const data = enc.encode(input)
  const digest = await crypto.subtle.digest('SHA-256', data)
  return b64url(digest)
}

function randStr(bytes = 32) {
  const arr = new Uint8Array(bytes)
  crypto.getRandomValues(arr)
  return b64url(arr.buffer)
}

export type OidcConfig = {
  issuer?: string
  authorizationEndpoint?: string
  tokenEndpoint?: string
  endSessionEndpoint?: string
  clientId?: string
  redirectUri?: string
  scope?: string
}

async function loadConfig(): Promise<OidcConfig> {
  const { data } = await api.get('/ui/oidc/config')
  const cfg: OidcConfig = data || {}
  if ((!cfg.authorizationEndpoint || !cfg.tokenEndpoint) && cfg.issuer) {
    try {
      const { data: meta } = await api.get('/ui/oidc/metadata')
      cfg.authorizationEndpoint = cfg.authorizationEndpoint || meta.authorization_endpoint
      cfg.tokenEndpoint = cfg.tokenEndpoint || meta.token_endpoint
      cfg.endSessionEndpoint = cfg.endSessionEndpoint || meta.end_session_endpoint
    } catch (error) {
      console.warn('Failed to hydrate OIDC metadata', error)
    }
  }
  return cfg
}

export async function startLogin(next?: string) {
  const cfg = await loadConfig()
  if (!cfg.authorizationEndpoint || !cfg.clientId) throw new Error('OIDC not configured')

  const codeVerifier = randStr(64)
  const codeChallenge = await sha256(codeVerifier)
  sessionStorage.setItem('veridoc:pkce_verifier', codeVerifier)
  if (next) sessionStorage.setItem('veridoc:login_next', next)

  const state = randStr(16)
  sessionStorage.setItem('veridoc:state', state)

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: cfg.clientId!,
    scope: cfg.scope || 'openid profile email',
    redirect_uri: cfg.redirectUri || window.location.origin + '/login',
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
    state,
  })
  const url = `${cfg.authorizationEndpoint}?${params.toString()}`
  window.location.assign(url)
}

export async function handleRedirectIfPresent(): Promise<boolean> {
  const url = new URL(window.location.href)
  const code = url.searchParams.get('code')
  const state = url.searchParams.get('state')
  if (!code) return false

  const expectedState = sessionStorage.getItem('veridoc:state')
  if (expectedState && state && expectedState !== state) {
    throw new Error('State mismatch')
  }

  const codeVerifier = sessionStorage.getItem('veridoc:pkce_verifier')
  const redirectUri = (await loadConfig()).redirectUri || window.location.origin + '/login'
  if (!codeVerifier) throw new Error('Missing code_verifier')

  const { data } = await api.post('/ui/oidc/token', { code, codeVerifier, redirectUri, grantType: 'authorization_code' })
  // Persist tokens
  const accessToken: string | undefined = data.access_token
  const idToken: string | undefined = data.id_token
  const refreshToken: string | undefined = data.refresh_token
  const expiresIn: number = data.expires_in || 3600
  if (accessToken) localStorage.setItem('veridoc:access_token', accessToken)
  if (idToken) localStorage.setItem('veridoc:id_token', idToken)
  if (refreshToken) localStorage.setItem('veridoc:refresh_token', refreshToken)
  const expAt = Date.now() + expiresIn * 1000
  localStorage.setItem('veridoc:token_expires_at', String(expAt))
  scheduleAutoRefresh(expiresIn)

  // Cleanup URL
  url.searchParams.delete('code')
  url.searchParams.delete('state')
  window.history.replaceState({}, '', url.pathname + url.search + url.hash)

  return true
}

export function clearTokens() {
  try {
    localStorage.removeItem('veridoc:access_token')
    localStorage.removeItem('veridoc:id_token')
    localStorage.removeItem('veridoc:refresh_token')
    localStorage.removeItem('veridoc:token_expires_at')
  } catch (error) {
    console.warn('Failed to clear OIDC tokens from localStorage', error)
  }
  try {
    sessionStorage.removeItem('veridoc:pkce_verifier')
    sessionStorage.removeItem('veridoc:state')
    sessionStorage.removeItem('veridoc:login_next')
  } catch (error) {
    console.warn('Failed to clear OIDC state from sessionStorage', error)
  }
}

function scheduleAutoRefresh(expiresInSec: number) {
  const skew = 60 // seconds before expiry to refresh
  const timeoutMs = Math.max((expiresInSec - skew) * 1000, 15_000)
  try {
    const anyWin = window as any
    if (anyWin.__veridoc_refresh_timer) {
      clearTimeout(anyWin.__veridoc_refresh_timer)
    }
    anyWin.__veridoc_refresh_timer = setTimeout(async () => {
      try {
        await refreshAccessToken()
      } catch (error) {
        console.warn('Scheduled token refresh failed', error)
      }
    }, timeoutMs)
  } catch (error) {
    console.warn('Failed to schedule token refresh timer', error)
  }
}

export function setupAutoRefreshOnLoad() {
  const exp = Number(localStorage.getItem('veridoc:token_expires_at') || '0')
  if (exp > Date.now()) {
    const remainingSec = Math.floor((exp - Date.now()) / 1000)
    scheduleAutoRefresh(remainingSec)
  }
}

export async function refreshAccessToken(): Promise<boolean> {
  const rt = localStorage.getItem('veridoc:refresh_token')
  if (!rt) return false
  const { data } = await api.post('/ui/oidc/token', { grantType: 'refresh_token', refreshToken: rt })
  const accessToken: string | undefined = data.access_token
  const idToken: string | undefined = data.id_token
  const refreshToken: string | undefined = data.refresh_token || rt
  const expiresIn: number = data.expires_in || 3600
  if (accessToken) localStorage.setItem('veridoc:access_token', accessToken)
  if (idToken) localStorage.setItem('veridoc:id_token', idToken)
  if (refreshToken) localStorage.setItem('veridoc:refresh_token', refreshToken)
  const expAt = Date.now() + expiresIn * 1000
  localStorage.setItem('veridoc:token_expires_at', String(expAt))
  scheduleAutoRefresh(expiresIn)
  return true
}

export function hasValidRefreshToken(): boolean {
  return !!localStorage.getItem('veridoc:refresh_token')
}

export async function logoutOidc(redirect = true) {
  try {
    const cfg = await loadConfig()
    if (!cfg.endSessionEndpoint) {
      try {
        const { data: meta } = await api.get('/ui/oidc/metadata')
        cfg.endSessionEndpoint = meta.end_session_endpoint
      } catch (error) {
        console.warn('Failed to load OIDC metadata during logout', error)
      }
    }
    const idToken = localStorage.getItem('veridoc:id_token') || undefined
    const postLogoutRedirect = (cfg.redirectUri || window.location.origin + '/login')
    clearTokens()
    if (redirect && cfg.endSessionEndpoint) {
      const p = new URLSearchParams()
      if (idToken) p.set('id_token_hint', idToken)
      p.set('post_logout_redirect_uri', postLogoutRedirect)
      const url = `${cfg.endSessionEndpoint}?${p.toString()}`
      window.location.assign(url)
    }
  } catch (error) {
    console.warn('OIDC logout failed; clearing tokens only', error)
    clearTokens()
  }
}
